// @flow

// force flow to type check this file - https://github.com/zapier/zapier/pull/31127
import React, { type ElementRef } from 'react';

import { Colors, Forms } from '@zapier/theme';
import { css, jsx } from '@emotion/core';

/* eslint-disable-line no-unused-vars */

/** @jsx jsx */

const textInputBaseCss = {
  ...Forms.field.typography,
  boxSizing: 'border-box',
  backgroundColor: Colors.white,
  borderColor: Colors.adamantium,
  borderRadius: 3,
  borderStyle: 'solid',
  borderWidth: 1,
  color: Colors.batmobile,
  fontFamily: 'inherit',
  margin: '0 !important', // FLX-71: we need `!important` to override global zapier/zapier styles 💩
  minHeight: Forms.field.minHeight,
  padding: `0 ${Forms.field.padding} !important`, // FLX-71: we need `!important` to override global zapier/zapier styles 💩
  verticalAlign: 'top',
  width: '100%',
  transition: 'background-color 0.3s ease',
  '&[disabled]': {
    opacity: 0.7,
  },
  '&:focus': {
    outline: 'none',
    borderColor: Colors.heroine,
  },
  '&[type="search"]': {
    WebkitAppearance: 'textfield',
  },
};

const errorCss = css({
  backgroundColor: `${Colors.status.error.bg} !important`, // FLX-71: we need `!important` to override global zapier/zapier styles 💩
  borderColor: `${Colors.status.error.border} !important`, // FLX-71: we need `!important` to override global zapier/zapier styles 💩
});

const readonlyCss = css({
  backgroundColor: `${Colors.storm} !important`, // FLX-71: we need `!important` to override global zapier/zapier styles 💩
});

const uppercaseCss = css({
  textTransform: 'uppercase !important', // FLX-71: we need `!important` to override global zapier/zapier styles 💩
});

type TextInputTypes =
  | 'email'
  | 'password'
  | 'tel'
  | 'text'
  | 'number'
  | 'search';

export type Props = {
  /** True if the value entered into this input field does not conform to the format expected by the application. */
  ariaInvalid?: boolean,
  /**
   * If the label text is visible on screen, we SHOULD use aria-labelledby and
   * SHOULD NOT use aria-label. Use aria-label only if the interface is such
   * that it is not possible to have a visible label on the screen. User agents
   * give precedence to aria-labelledby over aria-label when computing the
   * accessible name property.
   *
   * For more info:
   * https://www.w3.org/TR/2010/WD-wai-aria-20100916/states_and_properties#aria-labelledby
   */
  ariaLabel?: string,
  /** The element IDs of DOM elements which label this input. */
  ariaLabelledBy?: string,
  /** True if user input is required before a form can be submitted. */
  ariaRequired?: boolean,
  /** autocapitalize value */
  autoCapitalize?: string,
  /** autocomplete value */
  autoComplete?: string,
  /** autocorrect value */
  autoCorrect?: string,
  /** autofocus the input when rendered */
  autoFocus?: boolean,
  /** Supports SensitiveData HOC */
  className?: string,
  /** The default value to render initially. Useful to configure an initial value when working with uncontrolled components.  */
  defaultValue?: string,
  /** True if the value entered into this input is erroneous in some way. */
  hasError?: boolean,
  /** The ID to give this DOM element (so it can be e.g. referenced by a label). */
  id?: string,
  /** Used to forward refs */
  inputRef?: ElementRef<*>,
  /** Hide border if true */
  isBorderHidden?: boolean,
  /** True if the field is disabled */
  isDisabled?: boolean,
  /** True if the input accepts multiple lines of text. Will render a textarea. */
  isMultiLine?: boolean,
  /** True if the input should be readonly */
  isReadonly?: boolean,
  /** True if the input should be uppercase */
  isUppercase?: boolean,
  /** max length of text */
  maxLength?: number,
  /** html name attribute */
  name?: string,
  /** Callback to be executed when the input is blurred. */
  onBlur?: (event: SyntheticInputEvent<HTMLInputElement>) => void,
  /** Callback to be executed when the input's value is changed. */
  onChange?: (event: SyntheticInputEvent<HTMLInputElement>) => void,
  /** Callback to be executed when the input is focused. */
  onFocus?: (event: SyntheticInputEvent<HTMLInputElement>) => void,
  /** Callback to be executed when a key is pressed */
  onKeyDown?: (event: SyntheticInputEvent<HTMLInputElement>) => void,
  /** A hint to the user of what can be entered in the control. The placeholder text must not contain carriage returns or line-feeds. */
  placeholder?: string,
  /** Only used if isMultiLine prop is unspecified or false. Signifies the type of input being rendered. Corresponds to the supported subset of HTML input element types. */
  type?: TextInputTypes,
  /** spellcheck value */
  spellCheck?: string,
  /** Step interval of number controls. *Only used when type="number".* */
  step?: number,
  /** Controls the value displayed. */
  value?: string,
  /** Can the input be resized? For isMultiline only */
  resize?: 'horizontal' | 'vertical' | 'both' | 'none',
  /** How many rows? For isMultiLine only */
  rows?: number,
};

const multilineCss = ({ resize }: Props) =>
  css({
    padding: '10px !important', // FLX-71: we need `!important` to override styles above that are overriding global zapier/zapier styles 💩
    resize: resize || 'both',
  });

const noBorderCss = css({
  border: 'none !important', // FLX-71: we need `!important` to override global zapier/zapier styles 💩
  '&:focus': {
    borderColor: Colors.heroine,
  },
});

/**
 * A text input form field. Handles both `input[type=text]` and `textarea` use cases. Also handles `input[type=email]`, `input[type=password]`, and `input[type=tel]`.
 */
class TextInput extends React.Component<Props, {}> {
  static defaultProps = {
    type: 'text',
    isMultiLine: false,
  };

  render() {
    const { props } = this;
    const {
      ariaInvalid,
      ariaLabel,
      ariaLabelledBy,
      ariaRequired,
      autoCapitalize,
      autoComplete,
      autoCorrect,
      autoFocus,
      className,
      defaultValue,
      id,
      inputRef,
      isBorderHidden,
      isDisabled,
      isMultiLine,
      isReadonly,
      isUppercase,
      maxLength,
      name,
      onBlur,
      onChange,
      onFocus,
      onKeyDown,
      placeholder,
      type,
      spellCheck,
      step,
      rows,
      value,
    } = props;

    const textInputCss = css(
      textInputBaseCss,
      props.hasError && errorCss,
      isMultiLine && multilineCss(props),
      isReadonly && readonlyCss,
      isUppercase && uppercaseCss,
      isBorderHidden && noBorderCss
    );

    const Tag = isMultiLine ? 'textarea' : 'input';

    const inputType = isMultiLine ? undefined : type;
    const numberControls = (type === 'number' && { step }) || {};
    const multilineControls = isMultiLine ? { rows } : {};

    return (
      <Tag
        aria-invalid={ariaInvalid}
        aria-label={ariaLabel}
        aria-labelledby={ariaLabelledBy}
        aria-required={ariaRequired}
        autoCapitalize={autoCapitalize}
        autoComplete={autoComplete}
        autoCorrect={autoCorrect}
        autoFocus={autoFocus}
        className={className}
        css={textInputCss}
        defaultValue={defaultValue}
        id={id}
        maxLength={maxLength}
        name={name}
        onBlur={onBlur}
        onChange={onChange}
        onFocus={onFocus}
        onKeyDown={onKeyDown}
        placeholder={placeholder}
        readOnly={isReadonly ? 'readonly' : false}
        ref={inputRef}
        spellCheck={spellCheck}
        type={inputType}
        {...numberControls}
        {...multilineControls}
        disabled={isDisabled}
        value={value}
      />
    );
  }
}

export default TextInput;
